import axios from "axios";
import { jwtDecode } from "jwt-decode";

// Initialize token from localStorage
const token = localStorage.getItem("app_token");
let initialUser = null;

if (token) {
  try {
    initialUser = jwtDecode(token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } catch (error) {
    console.error("Invalid token:", error);
    localStorage.removeItem("app_token");
  }
}

export default {
  namespaced: true,

  state: {
    token: token || null,
    user: initialUser || { user_type: 0 },
    isInitialized: false
  },

  getters: {
    isAuthenticated: state => !!state.token,

    getUser: state => state.user,

    isAdmin: state => state.user && state.user.user_type === 3,

    tokenExpiryTime: state => {
      if (!state.token) return null;
      try {
        const decoded = jwtDecode(state.token);
        return decoded.exp; // Expiration time as Unix timestamp (in seconds)
      } catch (error) {
        console.error("Error decoding token:", error);
        return null;
      }
    },

    timeUntilTokenExpires: (state, getters) => {
      const expiryTime = getters.tokenExpiryTime;
      if (!expiryTime) return 0;
      const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
      return expiryTime - currentTime;
    },

    isTokenExpired: (state, getters) => {
      const timeUntilExpiry = getters.timeUntilTokenExpires;
      if (timeUntilExpiry <= 0) {
        console.warn("Token expired. Clearing token...");
        return true;
      }
      return false;
    }
  },

  mutations: {
    SET_TOKEN(state, token) {
      state.token = token;
      if (token) {
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      } else {
        delete axios.defaults.headers.common["Authorization"];
      }
    },

    SET_USER(state, user) {
      state.user = user || { user_type: 0 };
    },

    SET_INITIALIZED(state, value) {
      state.isInitialized = value;
    }
  },

  actions: {
    async login({ commit }, credentials) {
      try {
        const response = await axios.post("/token", credentials);
        const { access_token, refresh_token } = response.data;
        const decoded = jwtDecode(access_token);

        commit("SET_TOKEN", access_token);
        commit("SET_USER", decoded);
        
        localStorage.setItem("app_token", access_token);
        localStorage.setItem("app_refresh_token", refresh_token);

        return true;
      } catch (error) {
        console.error("Login error:", error);
        throw error;
      }
    },

    async refreshToken({ commit }) {
      const refresh_token = localStorage.getItem("app_refresh_token");
      if (!refresh_token) {
        console.warn("No refresh token available");
        return false;
      }

      try {
        const response = await axios.post("/refresh", { refresh_token });
        const { access_token } = response.data;
        const decoded = jwtDecode(access_token);

        commit("SET_TOKEN", access_token);
        commit("SET_USER", decoded);
        
        localStorage.setItem("app_token", access_token);

        return access_token;
      } catch (error) {
        console.error("Token refresh failed:", error);
        commit("SET_TOKEN", null);
        commit("SET_USER", null);
        localStorage.removeItem("app_token");
        localStorage.removeItem("app_refresh_token");
        return false;
      }
    },

    async checkAuth({ state, getters, dispatch }) {
      if (!state.token) {
        return false;
      }
  
      if (getters.isTokenExpired) {
        console.warn("Session expired, logging out...");
        await dispatch("logout");
        router.push("/"); // Redirect to the main route
        return false;
      }
  
      return true;
    },

    logout({ commit }) {
      commit("SET_TOKEN", null);
      commit("SET_USER", null);
      localStorage.removeItem("app_token");
      localStorage.removeItem("app_refresh_token");
    },
  }
};
